import React, { useState, useEffect, useRef } from 'react';
import { 
  LuArrowLeft, LuBox, LuLock, LuEye, LuEyeOff, 
  LuCheckCircle, LuShieldCheck, LuLogIn, LuChevronRight 
} from "react-icons/lu";
import { baseurl } from '../../baseurl';
import axios from 'axios';
import { AlertCircle, CheckCheck, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import "../../asset/css/reset-password.css";
import logo from "../../asset/img/white.png";


const SetPassword = () => {
  // State Management
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '', show: false });
  
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const passwordInputRef = useRef(null);

  useEffect(() => {
    if (passwordInputRef.current) passwordInputRef.current.focus();
    if (!token) {
      displayMsg('error', 'Invalid or missing reset token. Please request a new link.');
    }
  }, [token]);

  // Automatic Navigation
  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted, navigate]);

  const displayMsg = (type, text) => {
    setMsg({ type, text, show: true });
    setTimeout(() => setMsg(prev => ({ ...prev, show: false })), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      displayMsg('error', 'Session expired. Please request a new link.');
      return;
    }
    if (password.length < 8) {
      displayMsg('error', 'Password must be at least 8 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      displayMsg('error', 'Passwords do not match.');
      return;
    }

    setIsLoading(true);
    setMsg({ ...msg, show: false });

    try {
      const response = await axios.post(`${baseurl}resetPassword`, { 
        token: token,
        newPassword: password 
      });

      if (response.data.success) {
        setIsSubmitted(true);
      } else {
        displayMsg('error', response.data.message || 'Failed to update password.');
      }
    } catch (error) {
      const errorText = error.response?.data?.message || 'Link invalid or expired.';
      displayMsg('error', errorText);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-page-wrapper" style={{ background: '#f9fafb', minHeight: '100vh' }}>
      {/* Navigation Header */}
      <nav className='d-flex justify-content-between align-items-center sticky-top bg-white px-4 py-3 border-bottom'>
        <a href="/" className="  d-flex align-items-center gap-2" style={{ textDecoration: 'none', fontWeight: 'bold', fontSize: '20px', color: '#111827' }}>
          <div>
      <img src={logo} className='img-fluid' style={{maxHeight:"40px",}} />
    {/* <div className="sub-brand  text-dark">Project Management Dashboard</div> */}
    </div>
        </a>
        <a href="/login" className="back-link d-flex align-items-center gap-1 m-0" style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none' }}>
          <LuArrowLeft size={16} /> Back to Login
        </a>
      </nav>

      <div className="container d-flex flex-column align-items-center py-5">
        <div className="text-center mb-4">
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#111827' }}>Secure Your Account</h1>
          <p style={{ color: '#6b7280' }}>Create a new password to regain access</p>
        </div>

        {/* Form Card */}
        <div className="reset-card bg-white p-4 shadow-sm border" style={{ width: '100%', maxWidth: '450px', borderRadius: '16px' }}>
          
          {/* Global Alerts */}
          <div className={`alert-toast ${msg.show ? 'show' : ''}`} style={{ 
              display: msg.show ? 'flex' : 'none', 
              alignItems: 'center', gap: '10px', 
              padding: '12px', borderRadius: '8px', 
              marginBottom: '20px', 
              background: msg.type === 'error' ? '#fef2f2' : '#f0fdf4',
              color: msg.type === 'error' ? '#dc2626' : '#16a34a',
              fontSize: '14px', border: `1px solid ${msg.type === 'error' ? '#fecaca' : '#bbf7d0'}`
          }}>
            <AlertCircle size={18} />
            <span>{msg.text}</span>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
                  <label className="form-label">New Password</label>
                  <div className="input-with-icon">
                    <LuLock className="input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-input-reset"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      ref={passwordInputRef}
                      required
                    />
                    <div 
                      className="password-toggle" 
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ cursor: 'pointer', position: 'absolute', right: '15px', top: '12px', color: '#6366f1' }}
                    >
                      {showPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                    </div>
                  </div>
                </div>

            <div className="mb-4">
              <label className="form-label" style={{ fontWeight: '600', fontSize: '14px' }}>Confirm Password</label>
              <div className="position-relative">
                <LuShieldCheck className="position-absolute" style={{ left: '12px', top: '12px', color: '#9ca3af' }} />
                <input
                  type="password"
                  className="form-control px-5 py-2"
                  style={{ borderRadius: '10px', height: '45px', border: '1px solid #d1d5db' }}
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="btn w-100 py-2 d-flex align-items-center justify-content-center gap-2" 
              disabled={isLoading || !token}
              style={{ background: '#6366f1', color: '#fff', borderRadius: '10px', fontWeight: 'bold', border: 'none' }}
            >
              {isLoading ? <Loader2 size={20} className="animate-spin" /> : 'Reset Password'}
              {!isLoading && <LuChevronRight size={18} />}
            </button>
          </form>
        </div>

        <p className="mt-4 text-center" style={{ fontSize: '12px', color: '#9ca3af' }}>
          © 2026 Measro. All rights reserved. <br />
          Need help? <a href="/support" style={{ color: '#6366f1', textDecoration: 'none' }}>Contact Support</a>
        </p>
      </div>

      {/* SUCCESS MODAL OVERLAY */}
      {isSubmitted && (
        <div className="custom-modal-overlay" style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(17, 24, 39, 0.8)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div className="bg-white p-5 text-center shadow-lg" style={{ width: '90%', maxWidth: '400px', borderRadius: '24px', animation: 'modalIn 0.3s ease-out' }}>
            <div className="mb-4 d-inline-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#f0fdf4' }}>
              <CheckCheck size={48} color="#16a34a" />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#111827' }}>All Set!</h2>
            <p style={{ color: '#6b7280', marginBottom: '30px' }}>Your password has been changed. You will be redirected to the login page automatically.</p>
            
            <button 
              className="btn w-100 py-3 d-flex align-items-center justify-content-center gap-2" 
              onClick={() => navigate('/login')}
              style={{ background: '#6366f1', color: '#fff', borderRadius: '12px', fontWeight: 'bold', border: 'none' }}
            >
              <LuLogIn size={20} /> Login Now
            </button>
            <div className="mt-3" style={{ fontSize: '12px', color: '#9ca3af' }}>
              Redirecting in few seconds...
            </div>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default SetPassword;